import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Users, TrendingUp, ExternalLink, Building2, Lock, ArrowLeft, Calendar, UserPlus, Clock, Zap } from 'lucide-react';
import { toast } from 'sonner';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import analytics from '@/utils/analytics';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Format subscriber count (1200000 -> 1.2M)
const formatSubscribers = (count) => {
  if (!count) return '0';
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
  return count.toString();
};

// Calculate days ago
const daysAgo = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff < 7) return `${diff} days ago`;
  if (diff < 30) return `${Math.floor(diff / 7)} weeks ago`;
  return `${Math.floor(diff / 30)} months ago`;
};

// Check if item is new (detected within last 7 days)
const isNewItem = (dateStr) => {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  return diffDays <= 7;
};

// Check if item is very recent (within 48 hours)
const isVeryRecent = (dateStr) => {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  const now = new Date();
  const diffHours = (now - date) / (1000 * 60 * 60);
  return diffHours <= 48;
};

// Helper to get trust signal label based on mention count
const getTrustSignalLabel = (mentionCount) => {
  if (mentionCount >= 3) return 'Frequently featured across videos';
  if (mentionCount === 2) return 'Repeated sponsor';
  return 'Recently detected';
};

export const CreatorProfile = () => {
  const { creatorId } = useParams();
  const navigate = useNavigate();
  const [creator, setCreator] = useState(null);
  const [brands, setBrands] = useState([]);
  const [similarCreators, setSimilarCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [unlockType, setUnlockType] = useState('brands');
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  // Logged-in users get FULL access (no paywall until payment integration)
  const FREE_LIMIT = isLoggedIn ? 100 : 3;

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (response.data && response.data.user_id) {
          setIsLoggedIn(true);
        }
      } catch (error) {
        setIsLoggedIn(false);
      }
    };
    checkAuth();
  }, []);

  useEffect(() => {
    loadCreatorData();
  }, [creatorId]);

  const loadCreatorData = async () => {
    setLoading(true);
    try {
      // Add cache-busting timestamp to force fresh fetch
      const timestamp = Date.now();
      
      // Load creator with cache-busting
      const creatorRes = await axios.get(`${API}/creators/${creatorId}?_t=${timestamp}`);
      const creatorData = creatorRes.data;
      setCreator(creatorData);
      setLastRefreshed(new Date());
      
      // Track creator view
      analytics.creatorClick(creatorData.channel_name, creatorData.creator_id, 'creator_profile');
      
      // Load all brands to find relationships with cache-busting
      const brandsRes = await axios.get(`${API}/admin/brands?limit=100&_t=${timestamp}`);
      const allBrands = brandsRes.data.brands || [];
      
      // Find brands that have sponsored this creator
      const matchedBrands = allBrands.filter(brand => {
        const channels = brand.detected_from_channels || [];
        return channels.some(ch => 
          ch.toLowerCase().includes(creatorData.channel_name?.toLowerCase()) ||
          creatorData.channel_name?.toLowerCase().includes(ch.toLowerCase())
        );
      }).map(brand => ({
        ...brand,
        mention_count_for_creator: Math.max(1, Math.floor(brand.mention_count / Math.max(1, (brand.detected_from_channels || []).length)))
      }));
      
      // Sort by last_detected_at DESC (newest first), then mention_count DESC
      matchedBrands.sort((a, b) => {
        const dateA = new Date(a.last_detected_at || 0);
        const dateB = new Date(b.last_detected_at || 0);
        if (dateB - dateA !== 0) return dateB - dateA;
        return (b.mention_count_for_creator || 0) - (a.mention_count_for_creator || 0);
      });
      
      setBrands(matchedBrands);
      
      // Find similar creators (share brands with this creator)
      if (matchedBrands.length > 0) {
        const creatorBrandNames = new Set(matchedBrands.map(b => b.brand_name.toLowerCase()));
        
        // Get all creators with cache-busting
        const creatorsRes = await axios.get(`${API}/creators?limit=100&_t=${timestamp}`);
        const allCreators = creatorsRes.data.creators || [];
        
        // For each creator, count shared brands
        const creatorsWithSharedBrands = allCreators
          .filter(c => c.creator_id !== creatorId)
          .map(c => {
            // Find brands for this creator
            const creatorChannelName = c.channel_name?.toLowerCase() || '';
            const creatorBrands = allBrands.filter(brand => {
              const channels = brand.detected_from_channels || [];
              return channels.some(ch => 
                ch.toLowerCase().includes(creatorChannelName) ||
                creatorChannelName.includes(ch.toLowerCase())
              );
            });
            
            const sharedBrands = creatorBrands.filter(b => creatorBrandNames.has(b.brand_name.toLowerCase()));
            
            // Get the most recent brand detection for this creator
            const mostRecentBrand = creatorBrands.reduce((latest, brand) => {
              if (!latest) return brand;
              const latestDate = new Date(latest.last_detected_at || 0);
              const brandDate = new Date(brand.last_detected_at || 0);
              return brandDate > latestDate ? brand : latest;
            }, null);
            
            return {
              ...c,
              shared_brands_count: sharedBrands.length,
              shared_brand_names: sharedBrands.map(b => b.brand_name),
              last_brand_detected_at: mostRecentBrand?.last_detected_at
            };
          })
          .filter(c => c.shared_brands_count > 0)
          // Sort by last_brand_detected_at DESC, then shared_brands_count DESC
          .sort((a, b) => {
            const dateA = new Date(a.last_brand_detected_at || 0);
            const dateB = new Date(b.last_brand_detected_at || 0);
            if (dateB - dateA !== 0) return dateB - dateA;
            return b.shared_brands_count - a.shared_brands_count;
          });
        
        setSimilarCreators(creatorsWithSharedBrands);
      }
      
    } catch (error) {
      console.error('Failed to load creator:', error);
      toast.error('Failed to load creator profile');
    } finally {
      setLoading(false);
    }
  };

  const handleClaimProfile = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleBrandClick = (brand) => {
    analytics.brandClick(brand.brand_name, brand.detected_brand_id, 'creator_profile');
    navigate(`/brand/${encodeURIComponent(brand.brand_name)}`);
  };

  const handleCreatorClick = (c) => {
    analytics.creatorClick(c.channel_name, c.creator_id, 'similar_creators');
    navigate(`/creator/${c.creator_id}`);
  };

  const handleUnlock = (type) => {
    setUnlockType(type);
    setShowUnlockModal(true);
    analytics.ctaClick(`unlock_${type}`, 'creator_profile');
  };

  const handleRefresh = () => {
    loadCreatorData();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading creator profile...</p>
        </div>
      </div>
    );
  }

  if (!creator) {
    return (
      <div className="min-h-screen bg-slate-50">
        <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-indigo-600" />
              <span className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Sponsiq</span>
            </Link>
          </div>
        </nav>
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Creator Not Found</h1>
          <p className="text-slate-600 mb-8">We couldn't find this creator in our database.</p>
          <Link to="/brands/discover">
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Browse Creators
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const visibleBrands = brands.slice(0, FREE_LIMIT);
  const lockedBrands = brands.slice(FREE_LIMIT);
  const hasLockedBrands = lockedBrands.length > 0;
  
  const visibleSimilar = similarCreators.slice(0, FREE_LIMIT);
  const lockedSimilar = similarCreators.slice(FREE_LIMIT);
  const hasLockedSimilar = lockedSimilar.length > 0;

  const isDemo = ['Tech Explained', 'Fitness Journey', 'Beauty Basics'].includes(creator.channel_name) || creator.is_demo;

  // Count new brands
  const newBrandsCount = brands.filter(b => isNewItem(b.first_detected_at)).length;

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO 
        title={`${creator.channel_name} - Sponsorship Data`}
        description={`See which brands sponsor ${creator.channel_name}. Discover sponsorship relationships and find similar creators.`}
        path={`/creator/${creatorId}`}
      />

      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-indigo-600" />
            <span className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Sponsiq</span>
          </Link>
          <div className="flex items-center gap-2 md:gap-4">
            <Link to="/brands/discover">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-1" />
                All Creators
              </Button>
            </Link>
            <Link to="/explore-brands">
              <Button variant="ghost" size="sm">Discover Brands</Button>
            </Link>
            <Link to="/">
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" size="sm">
                Analyze Sponsorships
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Creator Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <Link to="/brands/discover" className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-800 text-sm font-medium mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Creators
          </Link>
          
          {isDemo && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-700">
                <span className="font-medium">Example Profile</span> — This is demo data for illustration.
              </p>
            </div>
          )}
          
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-2xl">
                {creator.channel_name?.charAt(0) || '?'}
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}} data-testid="creator-name">
                  {creator.channel_name}
                </h1>
                <div className="flex items-center gap-3 mt-1 text-slate-600">
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {formatSubscribers(creator.subscriber_count)} subscribers
                  </span>
                  {creator.niche && (
                    <>
                      <span>•</span>
                      <Badge className="bg-slate-100 text-slate-700 font-normal">{creator.niche}</Badge>
                    </>
                  )}
                  {creator.country && (
                    <>
                      <span>•</span>
                      <span>{creator.country}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex gap-2">
              {creator.youtube_link && (
                <a href={creator.youtube_link} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm">
                    <ExternalLink className="w-4 h-4 mr-1" />
                    YouTube
                  </Button>
                </a>
              )}
              {!creator.claimed && (
                <Button 
                  onClick={handleClaimProfile}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  size="sm"
                >
                  Claim Profile
                </Button>
              )}
            </div>
          </div>
          
          {/* Data proof and insights */}
          <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-slate-600">
            {brands.length > 0 && (
              <span className="flex items-center gap-1 text-indigo-600 font-medium">
                <Building2 className="w-4 h-4" />
                Worked with {brands.length} brand{brands.length > 1 ? 's' : ''}
              </span>
            )}
            <span className="flex items-center gap-1">
              <TrendingUp className="w-4 h-4 text-slate-400" />
              Detected from video mentions
            </span>
            {lastRefreshed && (
              <span className="flex items-center gap-1 text-slate-400">
                <Clock className="w-4 h-4" />
                Data refreshed {daysAgo(lastRefreshed.toISOString())}
              </span>
            )}
          </div>
          
          {/* Inline data disclaimer */}
          <p className="mt-4 text-xs text-slate-400">
            Data inferred from public YouTube content. Does not imply official partnership.
          </p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Brands Section */}
        <section className="mb-12">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>
                Brands Sponsoring This Creator
              </h2>
              {newBrandsCount > 0 && (
                <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                  {newBrandsCount} new this week
                </Badge>
              )}
            </div>
            <Button variant="ghost" size="sm" onClick={handleRefresh} className="text-slate-500 hover:text-slate-700">
              <Clock className="w-4 h-4 mr-1" />
              Refresh
            </Button>
          </div>
          <p className="text-slate-600 mb-6">
            Brands {creator.channel_name} has collaborated with (sorted by most recent)
          </p>

          {brands.length === 0 ? (
            <Card className="p-8 text-center bg-slate-50">
              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">Analyzing Sponsorships</h3>
              <p className="text-slate-500 max-w-md mx-auto">
                We're analyzing this creator's content to identify brand partnerships. Check back soon for updates.
              </p>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Visible Brands */}
              {visibleBrands.map((brand, index) => {
                const brandIsNew = isNewItem(brand.first_detected_at);
                const brandIsVeryRecent = isVeryRecent(brand.first_detected_at);
                
                return (
                  <Card 
                    key={brand.detected_brand_id || index}
                    className={`p-5 hover:shadow-md transition-shadow cursor-pointer ${brandIsVeryRecent ? 'ring-2 ring-emerald-200 bg-emerald-50/30' : ''}`}
                    onClick={() => handleBrandClick(brand)}
                    data-testid={`brand-card-${index}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-indigo-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>
                              {brand.brand_name}
                            </h3>
                            {brandIsVeryRecent && (
                              <Badge className="bg-emerald-500 text-white text-xs animate-pulse">
                                <Zap className="w-3 h-3 mr-0.5" />
                                Just Added
                              </Badge>
                            )}
                            {brandIsNew && !brandIsVeryRecent && (
                              <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                                New
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-sm text-slate-500">
                            {brand.niche_category && (
                              <span>{brand.niche_category}</span>
                            )}
                            {brand.last_detected_at && (
                              <>
                                <span>•</span>
                                <span>Last seen {daysAgo(brand.last_detected_at)}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <p className="text-lg font-bold text-indigo-600" style={{fontFamily: 'JetBrains Mono'}}>
                          {brand.mention_count_for_creator || 1}
                        </p>
                        <p className="text-xs text-slate-500">mentions</p>
                        <p className="text-xs text-slate-400 mt-1">
                          {getTrustSignalLabel(brand.mention_count_for_creator || 1)}
                        </p>
                      </div>
                    </div>
                  </Card>
                );
              })}

              {/* Locked Brands */}
              {hasLockedBrands && (
                <div className="relative">
                  <div className="space-y-4 filter blur-sm pointer-events-none">
                    {lockedBrands.slice(0, 2).map((brand, index) => (
                      <Card key={`locked-${index}`} className="p-5 opacity-60">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-slate-200 rounded-xl"></div>
                            <div>
                              <div className="h-5 w-32 bg-slate-200 rounded mb-2"></div>
                              <div className="h-4 w-24 bg-slate-100 rounded"></div>
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                  
                  <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-xl">
                    <div className="text-center p-6">
                      <Lock className="w-10 h-10 text-indigo-600 mx-auto mb-3" />
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">
                        +{lockedBrands.length} more sponsors
                      </h3>
                      <p className="text-slate-600 text-sm mb-4">
                        Unlock all brand partnerships
                      </p>
                      <Button 
                        onClick={() => handleUnlock('brands')}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white"
                        data-testid="unlock-brands-btn"
                      >
                        <Lock className="w-4 h-4 mr-2" />
                        Unlock All Sponsors
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Similar Creators Section */}
        {similarCreators.length > 0 && (
          <section className="mb-12">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>
                Similar Creators
              </h2>
            </div>
            <p className="text-slate-600 mb-6">
              Creators who work with the same brands (sorted by most recent activity)
            </p>

            <div className="space-y-4">
              {/* Visible Similar Creators */}
              {visibleSimilar.map((c, index) => {
                const creatorIsNew = isNewItem(c.last_brand_detected_at);
                
                return (
                  <Card 
                    key={c.creator_id}
                    className={`p-5 hover:shadow-md transition-shadow cursor-pointer ${creatorIsNew ? 'ring-2 ring-emerald-200 bg-emerald-50/30' : ''}`}
                    onClick={() => handleCreatorClick(c)}
                    data-testid={`similar-creator-${index}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                          {c.channel_name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>
                              {c.channel_name}
                            </h3>
                            {creatorIsNew && (
                              <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                                Recent Activity
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <Users className="w-4 h-4" />
                              {formatSubscribers(c.subscriber_count)}
                            </span>
                            {c.niche && (
                              <>
                                <span>•</span>
                                <span>{c.niche}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <p className="text-lg font-bold text-emerald-600" style={{fontFamily: 'JetBrains Mono'}}>
                          {c.shared_brands_count}
                        </p>
                        <p className="text-xs text-slate-500">shared brands</p>
                      </div>
                    </div>
                  </Card>
                );
              })}

              {/* Locked Similar Creators */}
              {hasLockedSimilar && (
                <div className="relative">
                  <div className="space-y-4 filter blur-sm pointer-events-none">
                    {lockedSimilar.slice(0, 2).map((c, index) => (
                      <Card key={`locked-similar-${index}`} className="p-5 opacity-60">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-slate-200 rounded-full"></div>
                            <div>
                              <div className="h-5 w-32 bg-slate-200 rounded mb-2"></div>
                              <div className="h-4 w-24 bg-slate-100 rounded"></div>
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                  
                  <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-xl">
                    <div className="text-center p-6">
                      <UserPlus className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">
                        +{lockedSimilar.length} similar creators
                      </h3>
                      <p className="text-slate-600 text-sm mb-4">
                        Discover more creators with shared sponsors
                      </p>
                      <Button 
                        onClick={() => handleUnlock('similar')}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="unlock-similar-btn"
                      >
                        <Lock className="w-4 h-4 mr-2" />
                        See Similar Creators
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* CTA Section */}
        <section className="bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-xl p-8 text-center">
          <h3 className="text-2xl font-bold text-white mb-3" style={{fontFamily: 'Manrope'}}>
            Find More Creators Like {creator.channel_name}
          </h3>
          <p className="text-indigo-100 mb-6">
            Discover creators in {creator.niche || 'similar niches'} perfect for brand sponsorships
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/brands/discover">
              <Button className="bg-white text-indigo-600 hover:bg-indigo-50 font-semibold px-6">
                Explore Creators
              </Button>
            </Link>
            <Link to="/explore-brands">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-6">
                Browse Brands
              </Button>
            </Link>
          </div>
        </section>
      </main>

      {/* Unlock Modal */}
      {showUnlockModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <Card className="max-w-md w-full mx-4 p-6">
            <div className="text-center">
              <Lock className="w-12 h-12 text-indigo-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">Premium Feature</h3>
              <p className="text-slate-600 mb-6">
                {unlockType === 'brands' 
                  ? 'Get full access to all brand partnerships and detailed sponsorship data.'
                  : 'Discover all similar creators and their shared brand relationships.'}
              </p>
              <div className="space-y-3">
                <Button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white">
                  Upgrade to Pro
                </Button>
                <Button 
                  variant="ghost" 
                  className="w-full"
                  onClick={() => setShowUnlockModal(false)}
                >
                  Maybe Later
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      <Footer showDisclaimer={true} />
    </div>
  );
};

export default CreatorProfile;
